/**
 * /sources — "Connected Slack accounts": manage connected Slack accounts (plan
 * slack-connector.md §7.2). One card per account: workspace, connected user,
 * status, and a disconnect action.
 *
 * Unlike Signal there are no per-chat rows, toggles, or history import: Slack
 * indexes every conversation you're in from the moment you connect (D5/D6), so
 * there is nothing to pick and nothing to backfill.
 *
 * Disconnect lives on the View page (/sources/slack/:id), not here — it revokes
 * the Slack token and reconnecting needs a fresh OAuth, which is too costly to
 * sit one mis-click away in a list. See connector-danger-zone.tsx.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Slack } from 'lucide-react';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { buttonVariants } from '@/components/ui/button';
import { type SlackAccount, type SlackAccountStatus, slackAccountApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const STATUS: Record<SlackAccountStatus, { label: string; tone: StatusPillTone }> = {
  connecting: { label: 'Connecting', tone: 'neutral' },
  working: { label: 'Connected', tone: 'active' },
  rate_limited: { label: 'Rate limited', tone: 'paused' },
  revoked: { label: 'Disconnected', tone: 'paused' },
  error: { label: 'Error', tone: 'danger' },
};

function AccountCard({ account }: { account: SlackAccount }) {
  const status = STATUS[account.status];

  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="green">
          <Slack className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-[15px]">
            {account.slackTeamName ?? 'Slack workspace'}
          </p>
          <p className="truncate font-mono text-[13px] text-faint">
            {account.slackUserName ?? '—'} · Connected{' '}
            {new Date(account.createdAt).toLocaleDateString()}
          </p>
        </div>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <Link
          to="/sources/$connector/$accountId"
          params={{ connector: 'slack', accountId: account.id }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
        >
          View
        </Link>
      </div>

      {account.status === 'error' && (
        <p className="mt-3 text-[13px] text-destructive">
          Brain lost this connection. Reconnect from Sources to resume indexing.
        </p>
      )}
    </div>
  );
}

export function SlackConnectedAccounts() {
  const { data: accounts } = useQuery({
    queryKey: qk.slackAccounts,
    queryFn: () => slackAccountApi.list(),
    // Slack may be unconfigured (503) — stay quiet rather than retrying.
    retry: false,
  });

  // Hide accounts the user already disconnected (revoked); render nothing at all
  // when there are none to show.
  const live = (accounts ?? []).filter((a) => a.status !== 'revoked');
  if (live.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="font-semibold text-base">Connected Slack accounts</h2>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Every channel, DM, and group DM you&apos;re in is indexed automatically. Slack Connect
        channels shared with other companies are included, and messages from people outside your
        organisation are marked External.
      </p>
      <div className="mt-4 flex flex-col gap-4">
        {live.map((a) => (
          <AccountCard key={a.id} account={a} />
        ))}
      </div>
    </div>
  );
}
